<template>
  <div class="position: relative;">
    <q-toolbar
      color="white"
      text-color="grey-9"
      class="top-page-toolbar"
      style="position: fixed; top: calc(69 + var(--top-offset, 0))px; left: 0; z-index: 10; box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);"
    >
      <q-toolbar-title></q-toolbar-title>

      <div>
        <q-btn
          :label="$t('preview')"
          color="tertiary"
          class="q-mr-md"
          flat
          @click="previewInvoice"
        />

        <q-btn
          v-if="editing && !editingDraft"
          color="primary"
          @click="createInvoice"
        >
          {{ $t('save') }}
        </q-btn>

        <q-btn-dropdown
          v-else
          :label="$t('create')"
          color="primary"
          split
          @click="createInvoice"
        >
          <q-list link>
            <q-item v-close-overlay>
              <q-item-main>
                <q-item-tile
                  label
                  @click.native="saveDraft"
                >
                  {{ $t('save_draft') }}
                </q-item-tile>
              </q-item-main>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
    </q-toolbar>

    <div class="layout-padding">
      <section class="invoicing-create">
        <header>
          <img
            v-if="activeTeam.settings.logo_url"
            :src="activeTeam.settings.logo_url"
            class="logo"
          >

          <span v-html="returnLine" class="return-line"></span>

          <div class="customer">
            <customer-select
              :types="customerTypes.ALL"
              :value="customer"
              :hasError="errors.has('customer')"
              :errorLabel="errors.first('customer')"
              inverted-light
              hide-underline
              @input="updateCustomer"
            />
            <customer-select
              :types="[customerTypes.CONTACT_PERSON]"
              :placeholder="$tc('contact_person', 1)"
              :companyFilter="customerId"
              :hasErrors="errors.has('contactPerson')"
              :errorLabel="errors.first('contactPerson')"
              v-model="contactPerson"
              v-if="customer && customer.type === customerTypes.COMPANY"
              class="q-mt-sm"
            />

            <customer-address-select
              v-if="customer"
              v-model="invoiceAddresses"
              :customer="customer"
              @reFetchCustomer="handleReFetchCustomer"
              class="q-mt-md"
            />
          </div>
        </header>

        <main>
          <div class="row items-end">
            <div class="invoice-title">
              <span v-show="!editTitle" class="headline q-my-none" @click="toggleInvoiceTitle">
                {{ invoiceTitle }}
              </span>

              <q-field
                v-show="editTitle"
                class="q-ma-none headline-input"
                :error="errors.has('invoice_title')"
                :error-label="errors.first('invoice_title')"
              >
                <q-input
                  ref="invoiceTitleInput"
                  v-model="invoiceTitle"
                  v-validate.disable="'required|max:255'"
                  name="invoice_title"
                  :data-vv-as="$t('invoice_title')"
                  :placeholder="$t('invoice_title')"
                  :disable="!editTitle"
                  @blur="handleBlurInvoiceTitle"
                  @keydown.enter="handleBlurInvoiceTitle"
                  autofocus
                  hide-underline
                />
              </q-field>

              <q-tooltip v-show="!editTitle">
                {{ $t('click_to_edit') }}
              </q-tooltip>
            </div>

            <div class="invoice-number">
              <span
                class="invoice-number-text"
                v-show="generateInvoiceNumber"
                @click="toggleInvoiceNumber"
              >
                {{ nextInvoiceNumber }}
              </span>

              <q-field
                v-show="!generateInvoiceNumber"
                :error="errors.has('invoice_number')"
                :error-label="errors.first('invoice_number')"
              >
                <q-input
                  v-model="invoiceNumber"
                  v-validate.disable="'max:255'"
                  name="invoice_number"
                  prefix="#"
                  :data-vv-as="$t('invoice_number')"
                  :placeholder="$t('invoice_number')"
                  :disable="generateInvoiceNumber"
                  :after="[{ icon: 'cancel', handler () { generateInvoiceNumber = true; invoiceNumber = null }}]"
                  @blur="checkInvoiceNumber"
                  autofocus
                  hide-underline
                  ref="invoiceNumberInput"
                />
              </q-field>

              <q-tooltip v-show="generateInvoiceNumber">
                {{ $t('invoice_number_tooltip') }}
              </q-tooltip>
            </div>
          </div>

          <div class="invoice-meta q-mt-sm">
            <q-field
              :error="errors.has('invoiceDate')"
              :error-label="errors.first('invoiceDate')"
              :label="$t('invoice_date')"
              :label-width="6"
              class="q-ma-none"
            >
              <q-datetime
                v-model="invoiceDate"
                type="date"
                v-validate.disable="'required'"
                name="invoiceDate"
                hide-underline
                :data-vv-as="$t('invoice_date')"
                :placeholder="$t('invoice_date')"
                :format="$t('date_format')"
                @change="updateDueDate"
              />
            </q-field>

            <div class="row justify-between">
              <div class="col-xs-12 col-md-5 delivery-time-select">
                <q-select
                  v-model="deliveryPeriod"
                  :options="deliveryTimeOptions"
                />
              </div>
              <div class="col-xs-12 col-md-6">
                <q-field
                  :error="errors.has('deliveryDate')"
                  :error-label="errors.first('deliveryDate')"
                  class="q-ma-none"
                >
                  <q-datetime
                    v-model="deliveryDate"
                    type="date"
                    v-validate.disable="'required'"
                    name="deliveryDate"
                    hide-underline
                    :data-vv-as="$t(deliveryDateLabel)"
                    :placeholder="$t(deliveryDateLabel)"
                    :format="$t('date_format')"
                  />
                </q-field>
              </div>
            </div>

            <q-field
              :error="errors.has('deliveryPeriodEnd')"
              :error-label="errors.first('deliveryPeriodEnd')"
              label=" "
              :label-width="6"
              class="q-ma-none"
              v-if="deliveryPeriod"
            >
              <q-datetime
                v-model="deliveryPeriodEnd"
                type="date"
                v-validate.disable="'required'"
                name="deliveryPeriodEnd"
                hide-underline
                :data-vv-as="$t('end')"
                :placeholder="$t('end')"
                :format="$t('date_format')"
              />
            </q-field>

            <q-field
              :error="errors.has('dueDate')"
              :error-label="errors.first('dueDate')"
              :label="$t('due_date')"
              :label-width="6"
              class="q-ma-none"
            >
              <q-datetime
                v-model="dueDate"
                type="date"
                name="dueDate"
                hide-underline
                clearable
                :data-vv-as="$t('due_date')"
                :placeholder="$t('due_date')"
                :format="$t('date_format')"
              />
            </q-field>

            <q-field
              :label="$tc('project', 1)"
              :label-width="6"
              class="q-ma-none"
            >
              <div v-if="!hasMultipleProjects">
                <project-indicator :project="projects ? projects[0] : undefined" clickable/>
                <project-popover
                  :active-project="projects ? projects[0] : undefined"
                  @select-project="handleSelectProject"
                />
              </div>

              <div v-else>
                <project-indicator v-for="(project, index) in projects" :key="index" :project="project"/>
              </div>
            </q-field>
          </div>

          <add-textarea-box
            v-model="noteTop"
            name="noteTop"
            v-validate.disable="'max:100000'"
            :placeholder="$t('note_top')"
            :additional-box-text="$t('or_use_template')"
            @clicked="$refs.topTemplateModal.open()"
            class="q-mt-xl"
            useMarkdown
          />

          <line-items
            v-for="(entry, index) in items[0].children"
            :key="index"
            :value="entry"
            :currency="currency"
            :show-heading-input="items[0].children.length > 1"
            :show-delete-button="items[0].children.length > 1"
            ref="itemsTable"
            class="q-mt-xl"
            @deleteBlock="handleDeleteBlock(index)"
            @deleteItem="(itemIndex) => handleDeleteItem(index, itemIndex)"
            @updateGroup="(value) => handleUpdateGroup(index, value)"
            @updateGroupHeading="(value) => handleUpdateGroupHeading(index, value)"
            @update="handleLineItemUpdate"
          />

          <div class="flex justify-center q-mt-lg">
            <q-btn-dropdown
              v-if="!showItemForm"
              :label="$t('new_line_item_button')"
              color="primary"
              split
              outline
              @click="handleAddItemButton"
            >
              <q-list link>
                <q-item @click.native="handleAddLineItemsBlock" v-close-overlay>
                  <q-item-main>
                    <q-item-tile label>{{ $t('invoice_block_add') }}</q-item-tile>
                  </q-item-main>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </div>

          <items-list-new-entry
            v-if="showItemForm"
            :currency="currency"
            :active-line-item="activeLineItem"
            type="invoice"
            class="q-mt-lg"
            @add="handleAddItem"
            @update="handleUpdateItem"
            @cancel="handleItemFormCancel"
          />

          <div v-if="errors.has('items')" class="on-right">
            <span class="text-negative">{{ $t('item_required') }}</span>
          </div>

          <invoice-price-summary
            :items="lineItems"
            :discount="discount"
            :cashDiscount="cashDiscount"
            class="q-mt-xl"
          />

          <div class="row justify-end q-mt-sm q-mb-xl">
            <q-btn-group flat outline>
              <q-btn
                v-if="discount === null"
                color="grey-7"
                flat
                outline
                dense
                no-caps
                @click="addDiscount"
              >
                {{ $t('add_discount') }}
              </q-btn>

              <q-btn
                v-else
                color="negative"
                flat
                outline
                dense
                no-caps
                @click="deleteDiscount"
              >
                {{ $t('delete_discount') }}
              </q-btn>

              <q-btn
                v-if="cashDiscount === null"
                color="grey-7"
                flat
                outline
                dense
                no-caps
                @click="addCashDiscount"
              >
                {{ $t('add_cash_discount') }}
              </q-btn>

              <q-btn
                v-else
                color="negative"
                flat
                outline
                dense
                no-caps
                @click="deleteCashDiscount"
              >
                {{ $t('delete_cash_discount') }}
              </q-btn>
            </q-btn-group>
          </div>

          <add-textarea-box
            v-model="noteBottom"
            name="noteBottom"
            v-validate.disable="'max:100000'"
            :placeholder="$t('note_bottom')"
            :additional-box-text="$t('or_use_template')"
            @clicked="$refs.bottomTemplateModal.open()"
            class="q-mt-xl"
            useMarkdown
          />
        </main>

        <div class="footer">
          <q-collapsible :label="$t('options')">
            <!-- Language -->
            <div class="row">
              <div class="col-12 col-md-6">
                <language-input
                  v-model="language"
                  :labelWidth="3"
                />
              </div>
            </div>
          </q-collapsible>
        </div>
      </section>
    </div>

    <text-template-modal
      ref="topTemplateModal"
      @selected="text => this.noteTop = text"
      model="invoice"
      initialPosition="top"
    />
    <text-template-modal
      ref="bottomTemplateModal"
      @selected="text => this.noteBottom = text"
      model="invoice"
      initialPosition="bottom"
    />
    <add-discount-dialog
      ref="addDiscountDialog"
      :currency="currency"
      @discount="handleAddDiscount"
    />
    <add-cash-discount-dialog
      ref="addCashDiscountDialog"
      :invoiceDate="invoiceDate"
      :dueDate="dueDate"
      @discount="handleAddCashDiscount"
    />
    <preview-modal ref="previewModal" :blob="previewBlob"/>
  </div>
</template>

<script>
  import { Notify, date } from 'quasar'
  import { mapActions, mapGetters } from 'vuex'
  import AddCashDiscountDialog from 'components/Invoices/Create/AddCashDiscountDialog.vue'
  import AddDiscountDialog from 'components/Invoices/Create/AddDiscountDialog.vue'
  import AddTextareaBox from 'components/Invoices/Create/AddTextareaBox.vue'
  import CustomerAddressSelect from 'components/Invoices/Create/CustomerAddressSelect.vue'
  import CustomerSelect from 'components/Invoices/Create/CustomerSelect.vue'
  import { CustomersClient } from 'src/api/Customers'
  import { InvoiceClient } from 'src/api/Invoices'
  import InvoicePriceSummary from 'components/Invoices/Create/InvoicePriceSummary.vue'
  import ItemsListNewEntry from 'components/Invoices/Create/ItemForm.vue'
  import LanguageInput from 'components/Common/Inputs/LanguageInput.vue'
  import LineItems from 'components/Invoices/Create/LineItems.vue'
  import PreviewModal from 'components/Invoices/Create/PreviewModal.vue'
  import ProjectIndicator from 'components/TimeTracking/ProjectIndicator.vue'
  import ProjectPopover from 'components/TimeTracking/ProjectPopover.vue'
  import { ProjectsClient } from 'src/api/Projects'
  import { QuoteClient } from 'src/api/Quotes'
  import TextTemplateModal from 'components/TextTemplates/TextTemplateModal.vue'
  import { TimeEntriesClient } from 'src/api/TimeEntries'
  import { createErrorFromResponse } from 'src/utils/error'
  import { customerTypes } from 'src/utils/customerTypes'
  import { general } from 'src/config'
  import i18n from 'src/i18n/i18n'
  import parseDate from 'date-fns/parse'
  import store from 'src/store'

  function replaceDecimalSign (value) {
    if (typeof value === 'string') {
      value = value.replace(/,/, '.')
    }

    return value
  }

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        activeInvoice: null,
        invoiceTitle: i18n.tc('invoice'),
        editTitle: false,
        customer: null,
        contactPerson: null,
        invoiceAddresses: {
          invoiceAddress: null
        },
        invoiceNumber: null,
        invoiceDate: null,
        deliveryDate: null,
        deliveryPeriodEnd: null,
        dueDate: null,
        noteTop: '',
        noteBottom: '',
        currency: general.base_currency,
        discount: null,
        cashDiscount: null,
        projects: null,
        quote: null,
        language: general.default_locale,
        invoiceSaved: false,
        successCallback: null,
        deliveryPeriod: false,
        previewBlob: null
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'autoGenerateInvoiceNumber']),
      ...mapGetters('createInvoice', ['items', 'showItemForm', 'activeLineItem']),

      customerId () {
        return this.customer ? this.customer.id : null
      },

      nextInvoiceNumber () {
        return `#${this.activeTeam.settings.current_invoice_number_formatted}`
      },

      teamAddress () {
        return this.activeTeam.settings.default_address
      },

      returnLine () {
        if (!this.teamAddress) {
          return ''
        }

        return [
          this.activeTeam.name,
          this.teamAddress.address_line_1,
          `${this.teamAddress.postal_code}&nbsp;${this.teamAddress.locality}`
        ]
          .join('&nbsp;|&nbsp;')
      },

      paymentDeadline () {
        return this.customer && this.customer.default_payment_deadline
          ? this.customer.default_payment_deadline
          : this.activeTeam.settings.default_payment_deadline
      },

      lineItems () {
        return this.items
          .filter(block => block.type === 'table')
          .flatMap(table => table.children)
          .flatMap(group => group.children)
      },

      mappedItems () {
        return this.items.map(block => {
          if (block.type !== 'table') {
            return block
          }

          return {
            ...block,
            children: block.children.map(group => ({
              ...group,
              children: group.children.map(lineItem => ({
                id: lineItem.id,
                item_name: lineItem.itemName,
                price: lineItem.price,
                quantity: replaceDecimalSign(lineItem.quantity),
                sales_tax: lineItem.salesTax,
                description: lineItem.description,
                item_number: lineItem.itemNumber,
                discount_absolute: lineItem.discountAbsolute,
                discount_relative: lineItem.discountRelative,
                unit_name: lineItem.unitName
              }))
            }))
          }
        })
      },

      hasMultipleProjects () {
        return Array.isArray(this.projects) && this.projects.length > 1
      },

      generateInvoiceNumber: {
        get () {
          return this.autoGenerateInvoiceNumber && (this.invoiceNumber === '' || this.invoiceNumber === null)
        },

        set (value) {
          this.setAutoGenerateInvoiceNumber(value)
        }
      },

      editing () {
        return this.activeInvoice !== null
      },

      editingDraft () {
        return this.editing && this.activeInvoice.status === 'draft'
      },

      deliveryDateLabel () {
        if (this.deliveryPeriod) {
          return 'start'
        }

        return 'delivery_date'
      },

      deliveryTimeOptions () {
        return [{
          label: i18n.t('delivery_date'),
          value: false
        }, {
          label: i18n.t('delivery_period'),
          value: true
        }]
      },

      customerTypes () {
        return customerTypes
      }
    },

    methods: {
      ...mapActions(['setAutoGenerateInvoiceNumber']),
      ...mapActions('createInvoice', [
        'loadItems',
        'addLineItemsBlock',
        'deleteBlock',
        'addLineItem',
        'deleteLineItem',
        'updateActiveLineItem',
        'clearActiveLineItem',
        'showCreateItemForm',
        'cancelItemForm',
        'updateGroup',
        'updateGroupHeading',
        'setActiveLineItem',
        'resetState'
      ]),

      addDiscount () {
        this.$refs.addDiscountDialog.open()
      },

      deleteDiscount () {
        this.discount = null
      },

      addCashDiscount () {
        this.$refs.addCashDiscountDialog.open()
      },

      deleteCashDiscount () {
        this.cashDiscount = null
      },

      updateCustomer (customer) {
        if (customer === null) {
          this.customer = null
          this.contactPerson = null
        } else if (customer.type === 'contact_person') {
          this.customer = customer.company.data
          this.customer.type = 'company'
          this.contactPerson = customer
        } else if (this.contactPerson &&
          this.contactPerson.company &&
          this.contactPerson.company.data.id !== customer.id) {
            this.customer = customer
            this.contactPerson = null
        } else {
          this.customer = customer
        }

        this.updateDueDate()
      },

      updateDueDate () {
        if (this.editing) {
          return
        }

        this.dueDate = date.addToDate(this.invoiceDate, { days: this.paymentDeadline })

        if (this.errors) {
          this.errors.remove('customer')
          this.errors.remove('invoice_address')
        }
      },

      checkInvoiceNumber () {
        if (this.invoiceNumber === '' || this.invoiceNumber === null) {
          this.invoiceNumber = null
          this.generateInvoiceNumber = true
        }
      },

      handleBlurInvoiceTitle () {
        if (this.invoiceTitle === '' || this.invoiceTitle === null) {
          this.invoiceTitle = this.$tc('invoice', 1)
        }

        this.editTitle = false
      },

      toggleInvoiceTitle () {
        this.editTitle = true
        this.$nextTick(() => {
          this.$refs.invoiceTitleInput.focus()
        })
      },

      toggleInvoiceNumber () {
        this.generateInvoiceNumber = false
        this.$nextTick(() => {
          this.$refs.invoiceNumberInput.focus()
        })
      },

      handleSelectProject (project) {
        this.projects = project ? [project] : null
      },

      prepareData () {
        let data = {
          'team_address_id': this.activeTeam.settings.default_address.id,
          'customer_id': this.customer.id,
          'contact_person_id': this.contactPerson ? this.contactPerson.id : null,
          'invoice_address_id': this.invoiceAddresses.invoiceAddress.id,
          'items': this.mappedItems,
          'invoice_number': this.invoiceNumber,
          'invoice_date': date.formatDate(this.invoiceDate, 'YYYY-MM-DD'),
          'delivery_date': date.formatDate(this.deliveryDate, 'YYYY-MM-DD'),
          'delivery_period_end': (this.deliveryPeriod && this.deliveryPeriodEnd) ? date.formatDate(this.deliveryPeriodEnd, 'YYYY-MM-DD') : null,
          'due_date': date.formatDate(this.dueDate, 'YYYY-MM-DD'),
          'invoice_title': this.invoiceTitle,
          'note_top': this.noteTop ? this.noteTop.replaceAll('</div>', '').replaceAll('<div>', '<br />') : null, // Fix for QEditor not supporting paragraphs
          'note_bottom': this.noteBottom ? this.noteBottom.replaceAll('</div>', '').replaceAll('<div>', '<br />') : null, // Fix for QEditor not supporting paragraphs
          'currency': this.currency,
          'quote_id': this.quote ? this.quote : null,
          'cash_discount': this.cashDiscount ? this.cashDiscount.amount : null,
          'cash_discount_limit': this.cashDiscount ? date.formatDate(this.cashDiscount.dateLimit, 'YYYY-MM-DD') : null,
          'project_id': (this.projects && this.projects.length === 1) ? this.projects[0].id : null, // Todo: Handle case with multiple projects
          'discount_relative': null,
          'discount_absolute': null,
          'language': this.language
        }

        if (this.discount !== null) {
          const attributeName = this.discount.type === 'rel' ? 'discount_relative' : 'discount_absolute'
          data[attributeName] = this.discount.type === 'rel' ? replaceDecimalSign(this.discount.value) : this.discount.value
        }

        return Promise.resolve(data)
      },

      saveDraft () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            data.status = 'draft'

            const client = new InvoiceClient()
            return this.activeInvoice
              ? client.update(this.activeInvoice.id, data)
              : client.include(['customer.addresses', 'items', 'project', 'project.customer']).create(data)
          })
          .then(response => {
            const invoice = response.data.data

            if (!this.activeInvoice && this.successCallback) {
              this.successCallback(invoice)
            }

            return invoice
          })
          .then(invoice => {
            this.invoiceSaved = true
            this.$q.notify({ message: this.$t('invoice_draft_saved'), type: 'positive' })

            if (!this.activeInvoice) {
              this.activeInvoice = invoice
            }
            this.$router.push({ name: 'edit-invoice', params: { invoice: invoice.id, teamId: invoice.team_id } })
          })
          .catch(error => {
            if (error.message !== 'validation') {
              createErrorFromResponse(error)
            }
          })
      },

      createInvoice () {
        const invoiceDateIsPast = this.invoiceDate < (new Date()).setHours(0, 0, 0, 0)

        if (this.editingDraft && invoiceDateIsPast) {
          this.$q.dialog({
            title: this.$t('invoice_date'),
            message: this.$t('invoice_date_past'),
            ok: this.$t('save'),
            cancel: this.$t('edit')
          }).then(this.storeInvoice)
            .catch(() => {
            })
        } else {
          return this.storeInvoice()
        }
      },

      storeInvoice () {
        const client = new InvoiceClient()

        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            data.status = 'open'

            return this.editing
              ? client.update(this.activeInvoice.id, data)
              : client.create(data)
          })
          .then(response => {
            return this.editingDraft
              ? client.setStatus(this.activeInvoice.id, { status_code: 'open' })
              : Promise.resolve(response)
          })
          .then(response => {
            const invoice = this.activeInvoice || response.data.data

            if (!this.activeInvoice && this.successCallback) {
              this.successCallback(invoice)
            }

            return invoice
          })
          .then(invoice => {
            this.invoiceSaved = true
            this.$q.notify({ message: this.editing ? this.$t('invoice_saved') : this.$t('invoice_created'), type: 'positive' })
            this.$router.push({
              name: 'invoice',
              params: { invoice: invoice.id, teamId: this.activeTeam.id }
            })
          })
          .catch(error => {
            if (error.message !== 'validation') {
              createErrorFromResponse(error)
            }
          })
      },

      previewInvoice () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              this.$q.notify({ message: this.$t('please_resolve_all_validation_errors'), type: 'warning' })
              throw new Error('validation')
            }

            return this.prepareData()
          })
          .then(data => {
            return new InvoiceClient()
              .pdfPreview(data)
          })
          .then(response => {
            const pdf = response.data
            const blob = new Blob([pdf], { type: 'application/pdf' })
            this.previewBlob = blob
            this.$refs.previewModal.open()
          })
          .catch(error => {
            console.log(error)
            if (error.message !== 'validation') {
              createErrorFromResponse('validation')
            }
          })
      },

      handleReFetchCustomer () {
        new CustomersClient()
          .include(['addresses'])
          .get(this.customer.id)
          .then(response => {
            this.customer = response.data.data[0]
          })

        if (this.errors) {
          this.errors.remove('invoice_address')
        }
      },

      handleAddDiscount (discount) {
        this.discount = discount
      },

      handleAddCashDiscount (cashDiscount) {
        this.cashDiscount = cashDiscount
      },

      setDefaults () {
        this.invoiceDate = new Date()
        this.deliveryDate = new Date()

        const days = (this.customer && this.customer.default_payment_deadline)
          ? this.customer.default_payment_deadline
          : this.activeTeam.settings.default_payment_deadline

        this.dueDate = date.addToDate(new Date(), { days })
        this.language = this.language ? this.language : this.activeTeam.settings.language
      },

      itemsFromResponse (items) {
        return items.map(block => {
          if (block.type !== 'table') {
            return block
          }

          return {
            ...block,
            children: block.children.map(group => ({
              ...group,
              children: group.children.map(lineItem => ({
                ...lineItem,
                id: lineItem.id,
                itemName: lineItem.item_name,
                price: lineItem.price,
                quantity: lineItem.quantity,
                salesTax: lineItem.sales_tax,
                description: lineItem.description,
                itemNumber: lineItem.item_number,
                discountAbsolute: lineItem.discount_absolute,
                discountRelative: lineItem.discount_relative,
                unitName: lineItem.unit_name
              }))
            }))
          }
        })
      },

      loadInvoice (invoice, isQuote = false) {
        this.invoiceNumber = invoice.invoice_number
        this.invoiceTitle = invoice.invoice_title
        this.invoiceDate = invoice.invoice_date ? parseDate(invoice.invoice_date.date) : null
        this.deliveryDate = invoice.delivery_date ? parseDate(invoice.delivery_date.date) : null
        this.deliveryPeriodEnd = invoice.delivery_period_end ? parseDate(invoice.delivery_period_end.date) : null
        this.dueDate = invoice.due_date ? parseDate(invoice.due_date.date) : null
        this.noteTop = invoice.note_top
        this.noteBottom = invoice.note_bottom
        this.currency = invoice.currency
        this.customer = invoice.customer ? invoice.customer.data : null
        this.contactPerson = invoice.contact_person ? invoice.contact_person.data : null
        this.quote = invoice.quote_id
        this.projects = invoice.project ? [invoice.project.data] : null
        this.language = invoice.language

        this.deliveryPeriod = !!invoice.delivery_period_end

        // Check if invoice is actually a quote
        // If it is a quote we do not want to load the bottom and top note because it is very likely different
        if (isQuote) {
          this.noteTop = invoice.note_top = null
          this.noteBottom = invoice.note_bottom = null
        }

        if (invoice.discount_absolute) {
          this.discount = {
            value: invoice.discount_absolute,
            type: 'abs'
          }
        } else if (invoice.discount_relative) {
          this.discount = {
            value: invoice.discount_relative,
            type: 'rel'
          }
        }

        if (invoice.invoice_address_id) {
          this.invoiceAddresses.invoiceAddress = invoice.customer.data.addresses.data.find(address => address.id === invoice.invoice_address_id)
        }

        if (invoice.cash_discount) {
          this.cashDiscount = {
            amount: invoice.cash_discount,
            dateLimit: parseDate(invoice.cash_discount_limit.date)
          }
        }

        this.loadItems(invoice.items.data)
      },

      handleAddLineItemsBlock () {
        this.addLineItemsBlock()
      },

      handleLineItemInput (data, index) {
        this.items[0].children.splice(index, 1, data)
      },

      handleDeleteBlock (index) {
        this.deleteBlock(index)
      },

      handleAddItemButton () {
        this.showCreateItemForm()
      },

      handleAddItem (item) {
        this.addLineItem(item)
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleUpdateItem (item) {
        this.updateActiveLineItem(item)
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleDeleteItem (groupIndex, itemIndex) {
        this.deleteLineItem({ groupIndex, itemIndex })
      },

      handleItemFormCancel () {
        this.cancelItemForm()
        this.clearActiveLineItem()
      },

      handleUpdateGroup (groupIndex, value) {
        this.updateGroup({ groupIndex, value })
      },

      handleUpdateGroupHeading (groupIndex, heading) {
        this.updateGroupHeading({ groupIndex, heading })
      },

      handleLineItemUpdate (item) {
        this.setActiveLineItem(item)
        this.showCreateItemForm()
      }
    },

    created () {
      this.$validator.attach({
        name: 'customer',
        rules: 'required',
        getter: () => {
          return this.customer
        },
        alias: this.$tc('customer', 1)
      })
      this.$validator.attach({
        name: 'invoice_address',
        rules: 'required',
        getter: () => {
          return this.invoiceAddresses.invoiceAddress
        },
        alias: this.$t('invoice_address')
      })
      this.$validator.attach({
        name: 'items',
        rules: 'required|invoicing_items',
        getter: () => {
          return this.items
        }
      })

      this.setDefaults()
    },

    watch: {
      activeInvoice (invoice) {
        this.loadInvoice(invoice)
      }
    },

    beforeRouteEnter (to, from, next) {
      if (from.name === 'create-invoice') {
        next()

        return
      }

      store.dispatch('createInvoice/resetState')
      store.dispatch('refreshTeamData')
      store.dispatch('listInvoiceTextTemplates', { pagination: { sortBy: 'id', descending: true } })

      if (to.params.hasOwnProperty('invoice')) {
        const invoiceId = to.params.invoice

        new InvoiceClient()
          .include(['customer.addresses', 'items', 'project', 'contact_person'])
          .get(invoiceId)
          .then(response => {
            next(vm => {
              vm.activeInvoice = response.data.data[0]
            })
          })
      } else if (to.query.hasOwnProperty('customer')) {
        const customerId = to.query.customer

        new CustomersClient()
          .include(['addresses'])
          .get(customerId)
          .then(response => next(vm => {
            vm.customer = response.data.data[0]
          }))
          .catch(() => {
            Notify.create({ message: i18n.t('partner_not_found_notification'), type: 'warning' })
            next({ name: 'create-invoice', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('quote')) {
        const quoteId = to.query.quote

        new QuoteClient()
          .include(['customer.addresses', 'items', 'project', 'contact_person'])
          .get(quoteId)
          .then(response => next(vm => {
            const quote = response.data.data[0]

            // map quote properties to their invoice counterparts
            quote.quote_id = quote.id
            quote.invoice_title = i18n.tc('invoice', 1)
            quote.invoice_address_id = quote.address_id

            vm.loadInvoice(quote, true)
            vm.setDefaults()
          }))
          .catch(() => {
            Notify.create({ message: i18n.t('quote_not_found_notification'), type: 'warning' })
            next({ name: 'create-invoice', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('copy')) {
        const invoiceId = to.query.copy

        new InvoiceClient()
          .include(['customer.addresses', 'items', 'project', 'project.customer'])
          .get(invoiceId)
          .then(response => {
            next(vm => {
              const invoice = response.data.data[0]
              const customer = invoice.customer ? invoice.customer.data : null
              const customerHasAddress = (customer, addressId) => {
                return customer.addresses.data.find(address => address.id === addressId)
              }

              invoice.invoice_number = null

              invoice.invoice_date = null
              invoice.due_date = null

              if (!invoice.customer_exists) {
                invoice.customer = null
                invoice.customer_id = null
                invoice.customer_exists = null

                invoice.invoice_address_id = null
              } else if (customer && !customer.addresses) {
                invoice.invoice_address_id = null
              } else if (customer && customer.addresses) {
                if (invoice.invoice_address_id && !customerHasAddress(customer, invoice.invoice_address_id)) {
                  invoice.invoice_address_id = null
                }
              }

              vm.loadInvoice(invoice)
              vm.setDefaults()
            })
          })
          .catch(() => {
            Notify.create({ message: i18n.t('partner_not_found_notification'), type: 'warning' })
            next({ name: 'create-invoice', params: { teamId: store.getters.activeTeam.id } })
          })
      } else if (to.query.hasOwnProperty('projects')) {
        const projects = to.query.projects

        const projectIds = Array.isArray(projects) ? projects : projects.split(',')
        const grouping = to.query.grouping || 'individual'

        const projectsPromise = new ProjectsClient()
          .include(['customer', 'customer.addresses'])
          .get(projectIds)
          .then(response => {
            return response.data.data
          })

        const openTimeEntriesPromise = Promise.all(
          projectIds.map(projectId => {
            return new ProjectsClient()
              .openEntries(projectId, grouping)
              .then(response => {
                return {
                  ...response.data.data,
                  project_id: parseInt(projectId)
                }
              })
          })
        )

        Promise.all([projectsPromise, openTimeEntriesPromise])
          .then(results => {
            const [projects, timeEntries] = results

            const openTimeEntries = timeEntries.flatMap(
              result => result.entries.map(
                entry => ({ ...entry, project_id: result.project_id })
              )
            )

            const openTimeEntriesIds = timeEntries.flatMap(result => result.ids)

            if (grouping === 'fixed') {
              next(vm => {
                // ToDo: A bit hacky because this is wrong if projects do not belong to the same customer
                if (projects[0].customer) {
                  vm.customer = projects[0].customer.data
                }

                const items = [
                  {
                    type: 'table',
                    children: [
                      {
                        type: 'group',
                        heading: null,
                        children: projects.map(project => ({
                          type: 'line-item',
                          quantity: 1,
                          itemName: project.name,
                          price: project.budget ? project.budget : 0,
                          salesTax: vm.activeTeam.settings.is_small_business ? 0 : 19
                        }))
                      }
                    ]
                  }
                ]

                store.dispatch('createInvoice/setItems', items)
              })
            } else {
              next(vm => {
                // ToDo: A bit hacky because this is wrong if projects do not belong to the same customer
                if (projects[0].customer) {
                  vm.customer = projects[0].customer.data
                }

                const items = [
                  {
                    type: 'table',
                    children: [
                      {
                        type: 'group',
                        heading: null,
                        children: openTimeEntries.map(timeEntry => {
                          const project = projects.find(project => project.id === timeEntry.project_id)
                          const rounding = to.query.hasOwnProperty('rounding') ? parseInt(to.query.rounding) : 2

                          return {
                            type: 'line-item',
                            quantity: Math.ceil((timeEntry.duration / (60 * 60)) * rounding) / rounding,
                            itemName: timeEntry.description || project.name,
                            price: project.rate,
                            salesTax: vm.activeTeam.settings.is_small_business ? 0 : 19,
                            unitName: i18n.tc('hour')
                          }
                        })

                      }
                    ]
                  }
                ]

                store.dispatch('createInvoice/setItems', items)

                vm.projects = projects

                vm.successCallback = invoice => {
                  new TimeEntriesClient()
                    .settle(openTimeEntriesIds, invoice.id)
                }
              })
            }
          })
      } else if (to.query.hasOwnProperty('project')) {
        const projectId = to.query.project

        new ProjectsClient()
          .include(['customer', 'customer.addresses'])
          .get(projectId)
          .then(response => {
            return response.data.data[0]
          })
          .then(project => {
            next(vm => {
              if (project.customer) {
                vm.customer = project.customer.data
              }

              vm.projects = [project]
            })
          })
      } else {
        next()
      }
    },

    beforeRouteLeave (to, from, next) {
      if (this.invoiceSaved) {
        this.cancelItemForm()
        this.clearActiveLineItem()

        next()
        return
      }

      this.$q.dialog({
        title: this.$t('leave_page_title'),
        message: this.$t('leave_page_message'),
        ok: this.$t('leave_page_ok'),
        cancel: this.$t('leave_page_cancel')
      }).then(() => {
        this.cancelItemForm()
        this.clearActiveLineItem()

        next()
      })
        .catch(() => next(false))
    },

    components: {
      AddTextareaBox,
      InvoicePriceSummary,
      CustomerSelect,
      CustomerAddressSelect,
      LanguageInput,
      LineItems,
      AddDiscountDialog,
      AddCashDiscountDialog,
      TextTemplateModal,
      ProjectIndicator,
      ProjectPopover,
      ItemsListNewEntry,
      PreviewModal
    }
  }
</script>

<style src="src/components/Invoices/Create/create-invoice.styl" lang="stylus"></style>
